import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule, CUSTOM_ELEMENTS_SCHEMA, Injector } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from './shared/shared.module';
import { Store } from './store';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NotificationsModule } from './notifications/notifications.module';
import { OverlayModule } from '@angular/cdk/overlay';
import { DirectivesModule } from './directives/directives.module';
import { ToastrModule } from 'ngx-toastr';
import { FloatingPillModule } from './order-create/floating-pill/floating-pill.module';
import { SupplierStepthroughModule } from './supplier-stepthrough/supplier-stepthrough.module';
import { AuthResetModule } from './auth-reset/auth-reset.module';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { NgSelectModule } from '@ng-select/ng-select';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { PipesModule } from './pipes/pipes.module';
import { YsErrorHandler } from './YsErrorHandler';
import { CalculatorsComponent } from './calculators/calculators.component';
import { GritSandVolumeCalculatorComponent } from './calculators/grit-sand-volume-calculator/grit-sand-volume-calculator.component';
import { BeddingMixCalculatorComponent } from './calculators/bedding-mix-calculator/bedding-mix-calculator.component';
import { SubbaseCoverageCalculatorComponent } from './calculators/subbase-coverage-calculator/subbase-coverage-calculator.component';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { HttpModule } from './http/http.module';
import { ModalsComponent } from './shared/modals/modals/modals.component';
import { ModalsModule } from './shared/modals/modals.module';
import { MatButtonModule } from '@angular/material/button';
import { ColorPickerModule } from 'ngx-color-picker';
import { ErrorRequestInterceptor } from './http/error-request.interceptor';
import { AppNameInterceptor } from './http/app-name.interceptor';
import { setAppInjector } from './app-injector';
import { SearchHistoryService } from './global-search/search-history.service';
import { DatePipe } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { SocketIoConfig, SocketIoModule } from 'ngx-socket-io';
import { environment } from '../environments/environment';
import { CspService } from './services/csp.service';
import { PosthogService } from './services/posthog.service';

const config: SocketIoConfig = { url: environment.socketPath, options: {} };
@NgModule({
    declarations: [
        AppComponent,
        CalculatorsComponent,
        GritSandVolumeCalculatorComponent,
        BeddingMixCalculatorComponent,
        SubbaseCoverageCalculatorComponent,
        ModalsComponent,
    ],
    imports: [
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        OverlayModule,
        DirectivesModule,
        SharedModule,
        // PERF: UserModule removed from eager imports — already lazy via loadChildren. Consumers import it locally.
        AuthResetModule,
        NotificationsModule,
        // PERF: RequestsModule removed from eager imports — already lazy via loadChildren.
        // PERF: RewardModule removed from eager imports — already lazy via loadChildren.
        MatButtonModule,
        ToastrModule.forRoot({
            preventDuplicates: true,
            progressBar: true,
            closeButton: true,
        }),
        // PERF: OrderCreateModule no longer eager — it is lazy via loadChildren ('/order' route).
        // Only FloatingPillModule (the persistent "new order" button) needs to be eager.
        // The dead <app-order-create> overlay was removed from app.component.html.
        FloatingPillModule,
        SupplierStepthroughModule,
        // PERF: CaseStudyModule removed from eager imports — already lazy via loadChildren.
        // PERF: AdviceModule removed from eager imports — already lazy via loadChildren.
        AngularSvgIconModule.forRoot(),
        PopoverModule.forRoot(),
        NgSelectModule,
        BsDatepickerModule.forRoot(),
        PipesModule,
        NgxDaterangepickerMd.forRoot(),
        // PERF: ReportingModule removed from eager imports — already lazy via loadChildren.
        HttpModule,
        ModalsModule,
        ColorPickerModule,
        MatDatepickerModule,
        MatNativeDateModule,
        SocketIoModule.forRoot(config),
    ],
    providers: [
        Store,
        DatePipe,
        { provide: ErrorHandler, useClass: YsErrorHandler },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorRequestInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AppNameInterceptor,
            multi: true,
        },
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent],
})
export class AppModule {
    static injector: Injector;

    constructor(
        injector: Injector,
        private searchHistory: SearchHistoryService,
        private cspService: CspService,
        private posthogService: PosthogService,
    ) {
        AppModule.injector = injector;
        setAppInjector(injector);
        searchHistory.loadHistory();
        this.cspService.initialize();
        // Live monitoring for the admin. No-ops when posthogKey is empty (local dev).
        // Init at module construction so it's ready before any component captures.
        this.posthogService.init(environment.posthogKey, {
            api_host: environment.posthogHost,
        });
    }
}
